import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fetchFileTree } from "@/store/codebaseSlice";
import { track } from "@/utils/events/tracker";
import { TEMPLATE_PROJECT_PATHS } from "@/utils/templateProjects";
import { DownloadIcon, GitHubLogoIcon, ResetIcon } from "@radix-ui/react-icons";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  getAxiosErorrMessage,
  logAxiosError,
  useActiveProject,
  useCanvasId,
  useIsEmailOnly,
  useIsGitlabConnected,
  useIsGithubConnected,
  useActiveCanvas,
} from "@/utils/utils";
import { fetchGitStatus } from "../../store/gitStatusSlice";
import { resetGithubState } from "@/store/gitStatusSlice";

// @ts-ignore
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EditorContext, useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useLoadCodebase, useStartServer } from "@/utils/devServerUtils";
import {
  ArrowDownToLine,
  Check,
  ChevronDown,
  EllipsisIcon,
  GitBranchIcon,
  HardDriveDownload,
  Link2,
  PlusIcon,
  RefreshCcw,
  X,
  Unlink,
} from "lucide-react";
import FileIcons from "../../utils/seti-ui-icons.json";
import CleanAndDiscardGitChangesModal from "./CleanAndDiscardGitChangesModal";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { useUser } from "@clerk/clerk-react";
import { useSession } from "@clerk/clerk-react";
import { Tooltip } from "react-tooltip";
import { GitProvider } from "@/store/gitStatusSlice";
import { getUnexpiredToken } from "@/utils/utils";
import DisconnectRepoModal from "../DisconnectRepoModal";
import CreateRepositoryModal, { Org } from "../CreateRepositoryModal";
import { Canvas } from "@/utils/types";

interface Props {}

const DEFAULT_PERMISSION_FAILURE_LINK =
  "https://github.com/apps/tempo-labs-ai/installations/new";

enum BOTTOM_SECTION {
  COMMIT,
  CREATE,
  CHECKOUT,
  NONE,
}

const GitSettings: React.FC<Props> = React.memo((props) => {
  const canvasId = useCanvasId();

  const { user } = useUser();

  const { triggerRefetchProjects } = useSupabase();

  // Redux state
  const gitStatus = useSelector((state: any) => state.gitStatus.gitStatus);
  const gitStatusLoadingStatus = useSelector(
    (state: any) => state.gitStatus.status
  );

  const canvas = useActiveCanvas();

  const status = gitStatus?.status;
  const remote = gitStatus?.remote;
  const local = gitStatus?.local;
  const localAheadBy = gitStatus?.localAheadBy;
  const remoteAheadBy = gitStatus?.remoteAheadBy;

  const remoteBranches = remote?.all || [];
  const remoteExists = remoteBranches.some(
    (branch: string) => branch === `origin/${local?.current}`
  );

  // Split branches into those that are local and those that are remote

  const localBranches = local?.all || [];
  const remoteBranchesNotLocal = useMemo(() => {
    return (remote?.all || [])
      .map((branch) => branch.replace("origin/", ""))
      .filter((branch: string) => {
        return !localBranches.includes(branch);
      });
  }, [remote?.all, localBranches]);

  const uncommittedChangedFiles = status?.files?.map((file: any) => file.path);

  // Project from DB
  const project = useActiveProject();

  const dispatch = useDispatch<any>();

  // Local state
  const [showSection, setShowSection] = useState(BOTTOM_SECTION.NONE);
  const [commitMessage, setCommitMessage] = useState("");
  const [branchInput, setBranchInput] = useState("");
  const [loadingBranch, setLoadingBranch] = useState(false);
  const [loadingPull, setLoadingPull] = useState(false);
  const [loadingCommit, setLoadingCommit] = useState(false);
  const [loadingPush, setLoadingPush] = useState(false);
  const [loadingDiscard, setLoadingDiscard] = useState(false);
  const [showPushToRepoModal, setShowPushToRepoModal] = useState(false);

  const [disconnectingRepo, setDisconnectingRepo] = useState(false);
  const [showDisconnectConfirmModal, setShowDisconnectConfirmModal] =
    useState(false);
  const [originatingProviderForModal, setOriginatingProviderForModal] =
    useState<GitProvider | null>(null);

  const codeEditorSelectedFile = useCodebaseStore((state) => state.editor)?.[
    EditorContext.Code
  ]?.selectedFile;
  const openFile = useCodebaseStore((state) => state.openFile);
  const setAllLookups = useCodebaseStore((state) => state.setAllLookups);
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );

  const startServer = useStartServer();
  const loadCodebase = useLoadCodebase();

  // github orgs + github user to create repo in org or just in user account
  const [userGithubOrgs, setUserGithubOrgs] = useState<Org[]>([]);
  const [userGitlabOrgs, setUserGitlabOrgs] = useState<Org[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const [initialModalOrg, setInitialModalOrg] = useState<Org | null>(null);

  const { session } = useSession();

  const repoNotConnected = useMemo(() => {
    if (!project?.repoPath) {
      return;
    }

    return TEMPLATE_PROJECT_PATHS.includes(project.repoPath?.toLowerCase());
  }, [project]);

  const isEmailOnly = useIsEmailOnly();
  const isGitlabConnected = useIsGitlabConnected();
  const isGithubConnected = useIsGithubConnected();

  useEffect(() => {
    const fetchUserOrgs = async () => {
      if (!session) return;
      try {
        const supabaseJWT = await getUnexpiredToken(session);

        if (isGitlabConnected) {
          const url = `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/gitProvider/user/orgs?gitProvider=gitlab`;

          const externalGitlabAccount = user?.externalAccounts?.find(
            (account: any) =>
              account.provider === "gitlab" ||
              account.provider === "oauth_gitlab"
          );

          if (!externalGitlabAccount || !externalGitlabAccount.username) {
            // username is necessary to create repos
            console.error("No Gitlab account found");
            return;
          }

          const gitlabUserOrg = {
            login: externalGitlabAccount?.username,
            id: externalGitlabAccount?.id,
            avatar_url: externalGitlabAccount?.imageUrl,
            groupNamespaceId: null,
          };

          const res = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          });

          const gitlabOrgs = res.data.map((org: any) => ({
            login: org.name,
            id: org.id,
            avatar_url: org.avatar_url,
            groupNamespaceId: org.id,
          }));

          setUserGitlabOrgs([gitlabUserOrg, ...gitlabOrgs]);
          setSelectedOrg(gitlabUserOrg);
        }

        if (isGithubConnected) {
          const externalGithubAccount = user?.externalAccounts?.find(
            (account: any) => account.provider === "github"
          );

          if (!externalGithubAccount || !externalGithubAccount.username) {
            console.error("No Github account found");
            return;
          }

          const githubUserOrg = {
            login: externalGithubAccount?.username,
            id: externalGithubAccount?.id,
            avatar_url: externalGithubAccount?.imageUrl,
          };
          const url = `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/gitProvider/user/orgs?gitProvider=github`;

          const res = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          });

          setUserGithubOrgs([githubUserOrg, ...res.data]);
          setSelectedOrg(githubUserOrg);
        }
      } catch (err) {
        console.error("Failed to fetch user orgs", err);
      }
    };

    fetchUserOrgs();
  }, []);

  useEffect(() => {
    if (showSection !== BOTTOM_SECTION.COMMIT) {
      setCommitMessage("");
    }

    if (
      ![BOTTOM_SECTION.CHECKOUT, BOTTOM_SECTION.CREATE].includes(showSection)
    ) {
      setBranchInput("");
    }
  }, [showSection]);

  useEffect(() => {
    // Clear all loading statuses when the git status is finished loading
    if (gitStatusLoadingStatus === "loading") {
      return;
    }

    if (loadingBranch) {
      setLoadingBranch(false);
      setShowSection(BOTTOM_SECTION.NONE);
    }

    if (loadingCommit) {
      setLoadingCommit(false);
      setCommitMessage("");
    }

    if (loadingDiscard) {
      setLoadingDiscard(false);
    }

    if (loadingPull) {
      setLoadingPull(false);
    }

    if (loadingPush) {
      setLoadingPush(false);
    }
  }, [gitStatusLoadingStatus]);

  const checkoutBranch = (branch: string) => {
    if (!branch) {
      return;
    }

    setLoadingBranch(true);

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/git/gitCheckout`,
          {
            branch,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          setAllLookups({ lookups: res.data, canvasId });
          dispatch(
            fetchGitStatus({
              canvasId,
              session,
            })
          );
          dispatch(fetchFileTree({ session, canvasId }));
          startServer({
            forceRestart: true,
          });
        })
        .catch((err: any) => {
          setLoadingBranch(false);
          logAxiosError("Failed to checkout branch", err);
          toast.error(
            "Failed to checkout branch: " + getAxiosErorrMessage(err)
          );
        });
    });
  };

  const createBranch = (branch: string) => {
    if (!branch) {
      return;
    }

    setLoadingBranch(true);

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/git/gitCreateBranch`,
          {
            branch,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          // Refetch the git status
          dispatch(
            fetchGitStatus({
              session,
              canvasId,
            })
          );
        })
        .catch((err: any) => {
          setLoadingBranch(false);
          logAxiosError("Failed to create branch", err);
          toast.error("Failed to create branch: " + getAxiosErorrMessage(err));
        });
    });
  };

  const pull = () => {
    if (!remoteAheadBy) {
      return;
    }

    setLoadingPull(true);

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/git/gitPull`,
          {
            authorEmail: user?.primaryEmailAddress?.emailAddress,
            authorName: user?.username,
            branch: local?.current,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          setAllLookups({ lookups: res.data, canvasId });
          dispatch(
            fetchGitStatus({
              session,
              canvasId,
            })
          );
          dispatch(
            fetchFileTree({
              canvasId,
              session,
            })
          );
          startServer({
            forceRestart: true,
          });
        })
        .catch((err: any) => {
          setLoadingPull(false);
          logAxiosError("Failed to pull", err);
          let errorMessage = getAxiosErorrMessage(err);
          let reason = err.response?.data?.reason;

          if (reason) {
            toast.error(`Unable to pull: ${reason}`);
          } else {
            toast.error(`Failed to pull: ${errorMessage}`);
          }
        });
    });
  };

  const push = () => {
    if (remoteAheadBy) {
      // Need to pull before pushing
      toast.error("You need to pull changes before pushing.");
      return;
    }

    if (!localAheadBy && remoteExists) {
      return;
    }

    setLoadingPush(true);

    track("GIT_PUSH");

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/git/gitPush`,
          {},
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          // Refetch the git status
          dispatch(
            fetchGitStatus({
              session,
              canvasId,
            })
          );
        })
        .catch((err: any) => {
          setLoadingPush(false);
          logAxiosError("Failed to push", err);
          toast.error("Failed to push: " + getAxiosErorrMessage(err));
        });
    });
  };

  const commit = () => {
    if (!commitMessage) {
      return;
    }

    setLoadingCommit(true);

    track("GIT_COMMIT");

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/git/gitCommit`,
          {
            commitMessage,
            authorEmail: user?.primaryEmailAddress?.emailAddress,
            authorName: user?.username,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          // Refetch the git status
          dispatch(
            fetchGitStatus({
              session,
              canvasId,
            })
          );
        })
        .catch((err: any) => {
          setLoadingCommit(false);
          logAxiosError("Failed to commit", err);
          toast.error("Failed to commit: " + getAxiosErorrMessage(err));
        });
    });
  };

  const discardChanges = (file?: string) => {
    if (!uncommittedChangedFiles?.length) {
      return;
    }

    let requestParams = {};

    if (file) {
      requestParams = {
        files: [file],
      };
    }

    setLoadingDiscard(true);

    track("GIT_CLEAN");

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/git/gitClean`,
          requestParams,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then(async (res) => {
          await startServer({
            forceRestart: true,
          });
        })
        .catch((err: any) => {
          setLoadingDiscard(false);
          logAxiosError("Failed to discard changes", err);
          toast.error(
            "Failed to discard changes: " + getAxiosErorrMessage(err)
          );
        });
    });
  };

  const validBranches = {
    local: localBranches,
    remote: remoteBranchesNotLocal,
  };

  const textareaRef = useRef(null);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    // Reset the height to "auto" to get the correct scrollHeight
    // @ts-expect-error TS2339
    textarea.style.height = "auto";
    // Set height based on scroll height, but not exceeding max-height
    const maxHeight = 160; // Assuming max-h-40 is 10rem or 160px, adjust this value as necessary
    // @ts-expect-error TS2339
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  };

  // Adjust the height on mount and whenever the commitMessage changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [commitMessage]);

  const [changesExpanded, setChangesExpanded] = useState(true);

  const branchModifySection =
    showSection === BOTTOM_SECTION.CREATE ||
    showSection === BOTTOM_SECTION.CHECKOUT;

  const getBottomSection = () => {
    if (!canvas) {
      return null;
    }

    if (branchModifySection) {
      return (
        <div className="flex flex-col gap-y-4 p-2" key="create-branch-section">
          <div className="flex flex-row justify-between">
            <div className="text-xs text-secondary-900 font-semibold">
              {showSection === BOTTOM_SECTION.CHECKOUT
                ? "Checkout Existing Branch"
                : "Create New Branch"}
            </div>
            <X
              className="w-4 h-4 cursor-pointer text-secondary-700 hover:text-secondary-500"
              onClick={() => setShowSection(BOTTOM_SECTION.NONE)}
            />
          </div>
          <input
            className="bg-secondary-200 border-secondary-400 rounded-sm px-2 py-2 text-xs focus:outline-none"
            value={branchInput}
            onChange={(e: any) => setBranchInput(e.target.value)}
            placeholder="Branch name"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!loadingBranch) {
                  checkoutBranch(branchInput);
                }
              }
            }}
          />
          {showSection === BOTTOM_SECTION.CHECKOUT ? (
            <Button
              className="bg-secondary-200 space-x-1"
              variant="outline"
              disabled={loadingBranch}
              onClick={() => checkoutBranch(branchInput)}
              size="sm"
            >
              {loadingBranch ? <Spinner className="w-3 h-3" /> : null}
              Checkout
            </Button>
          ) : (
            <Button
              className="bg-secondary-200 space-x-1"
              variant="outline"
              disabled={loadingBranch}
              onClick={() => createBranch(branchInput)}
              size="sm"
            >
              {loadingBranch ? <Spinner className="w-3 h-3" /> : null}
              Create
            </Button>
          )}
        </div>
      );
    }

    let toReturn = [];

    if (!repoNotConnected) {
      toReturn = [
        // @ts-expect-error TS2322
        <div
          className="flex flex-row justify-between items-center space-x-2 p-2 text-secondary-800 text-xs border-b border-secondary-300"
          key={"branch-selector-row"}
        >
          <GitBranchIcon className="w-4 h-4 min-h-4 min-w-4 text-secondary-700" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex grow flex-row cursor-pointer items-center space-x-0.5 rounded-sm text-2xs">
                <div className="text-secondary-900">{local?.current}</div>
                <ChevronDown className="w-4 h-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={cn(
                "z-[9999]",
                showDisconnectConfirmModal && "opacity-50 cursor-not-allowed"
              )}
              align="start"
              sideOffset={5}
              alignOffset={0}
            >
              <ScrollArea
                className={cn(
                  "flex flex-col max-h-[400px] overflow-y-scroll -mr-2.5"
                )}
              >
                <div className="divide-y">
                  {validBranches.local.map((branch: string) => {
                    return (
                      <div
                        className="relative flex gap-2.5 select-none items-center p-2.5 text-xs outline-none focus:bg-accent focus:text-accent-foreground hover:bg-secondary-n-950 cursor-pointer"
                        key={branch}
                        itemType="local"
                        onClick={() => checkoutBranch(branch)}
                      >
                        {branch}
                      </div>
                    );
                  })}
                  {validBranches.remote.map((branch: string) => {
                    return (
                      <div
                        className="relative flex gap-2.5 select-none items-center p-2.5 text-xs outline-none focus:bg-accent focus:text-accent-foreground hover:bg-secondary-n-950 cursor-pointer"
                        key={branch + "tempo-remote"}
                        onClick={() => checkoutBranch(branch)}
                      >
                        <HardDriveDownload className="w-3 h-3" />
                        {branch}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
          <div
            className="flex flex-row space-x-0.5 items-center outline transition-color duration-300 ease-in-out outline-secondary-500 hover:outline-secondary-700 p-0.5 rounded-sm hover:bg-secondary-300 cursor-pointer"
            onClick={pull}
            data-tooltip-id="tooltip"
            data-tooltip-content={`Pull changes from remote${
              remoteAheadBy ? ` (${remoteAheadBy} commits behind)` : ""
            }`}
          >
            {repoNotConnected ? (
              <span>Sync</span>
            ) : loadingPull ? (
              <Spinner className="w-3 h-3 text-secondary-600 fill-secondary-200" />
            ) : (
              <ArrowDownToLine className="w-3 h-3" />
            )}
            {remoteAheadBy && <span>{remoteAheadBy}</span>}
          </div>
          <RefreshCcw
            className="w-3 h-3 min-w-3 min-h-3 cursor-pointer hover:text-secondary-600"
            onClick={() => {
              if (!session) {
                return;
              }

              dispatch(
                fetchGitStatus({
                  canvasId,
                  session,
                })
              );
            }}
            data-tooltip-id="tooltip"
            data-tooltip-content="Refresh git status"
          />
          <PlusIcon
            className="w-4 h-4 min-w-4 min-h-4 cursor-pointer hover:text-secondary-600"
            onClick={() => setShowSection(BOTTOM_SECTION.CREATE)}
            data-tooltip-id="tooltip"
            data-tooltip-content="Create new branch"
          />
        </div>,
      ];
    }

    if (uncommittedChangedFiles?.length) {
      // Commit section
      if (!repoNotConnected) {
        toReturn = toReturn.concat(
          // @ts-expect-error TS2769
          <div className="p-4 border-b border-secondary-300 flex flex-col space-y-2">
            <textarea
              className={
                "bg-secondary-200 border-secondary-400 rounded-sm h-auto max-h-40 resize-none p-2 text-xs focus:outline-none"
              }
              placeholder="Message (Enter to commit)"
              value={commitMessage}
              onChange={(e: any) => setCommitMessage(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!loadingCommit) {
                    commit();
                  }
                }
              }}
              ref={textareaRef}
            />
            <Button
              className="bg-secondary-200 space-x-1"
              key="commit-button"
              variant="outline"
              disabled={loadingCommit}
              onClick={commit}
              size="sm"
              data-tooltip-id="tooltip"
              data-tooltip-content={`Commit changes on ${
                gitStatus?.local?.current || "current branch"
              }`}
            >
              {loadingCommit ? (
                <Spinner className="w-3 h-3 text-secondary-600 fill-secondary-200" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              <span>Commit</span>
            </Button>
          </div>
        );
      }

      toReturn = toReturn.concat([
        // @ts-expect-error TS2769
        <div
          key="changes-section"
          className="flex flex-row justify-between items-center hover:bg-secondary-500 p-2 cursor-pointer"
          onClick={() => setChangesExpanded((prev) => !prev)}
        >
          <div className="flex flex-row space-x-1 items-center">
            <ChevronDown
              strokeWidth={1}
              className={cn(
                "w-5 h-5 text-secondary-800 cursor-pointer transition-all duration-300 ease-in-out",
                changesExpanded ? "rotate-0" : "rotate-[-90deg]"
              )}
            />
            <div className="text-secondary-900 text-xs font-bold">Changes</div>
          </div>
          <div className="rounded-full bg-secondary-400 w-6 h-6 text-2xs text-center flex justify-center items-center">
            {uncommittedChangedFiles.length}
          </div>
        </div>,
      ]);

      if (changesExpanded) {
        toReturn = toReturn.concat([
          // @ts-expect-error TS2769
          <div className="p-2 space-y-2 flex flex-col" key={"changes-expanded"}>
            <div className="">
              {...uncommittedChangedFiles?.map((filename) => {
                const dir = filename.split("/").slice(0, -1).join("/");
                const ext = filename.split(".").pop();
                const base = filename.split("/").pop();

                const indexOfCanvasId = dir.indexOf(canvasId);
                const dirFromBaseProject = dir.substring(
                  indexOfCanvasId + canvasId.length + 1
                );

                const lastTwoDirs = dirFromBaseProject
                  .split("/")
                  .slice(-2)
                  .join("/");

                const frontTruncatedDir =
                  lastTwoDirs.length > 30
                    ? `...${lastTwoDirs.substring(lastTwoDirs.length - 30)}`
                    : lastTwoDirs;

                const iconForExt = FileIcons[ext];

                const iconToUse =
                  iconForExt ||
                  "/icons/file-extension-items/seti-ui/default.svg";

                return (
                  <div
                    className={`commit-change min-w-0 ${
                      codeEditorSelectedFile?.filename === filename &&
                      codeEditorSelectedFile?.diffType
                        ? "selected"
                        : ""
                    }`}
                    key={`commit-change-${filename}`}
                    onClick={() => {
                      openFile({
                        file: {
                          filename: filename,
                          diffType: true,
                          context: EditorContext.Code,
                        },
                        jumpToTab: true,
                      });
                    }}
                  >
                    <div className="flex flex-row justify-between w-full relative items-center">
                      <div className="flex flex-row gap-x-1 min-w-0">
                        <div>
                          <img
                            alt={ext}
                            src={iconToUse}
                            width="20"
                            height="20"
                            style={{
                              boxSizing: "initial",
                            }}
                            className="max-w-none"
                          />
                        </div>
                        <div className="commit-change-filename truncate">
                          {base}
                        </div>
                        <div className="text-secondary-800 text-3xs pr-2 truncate">
                          {frontTruncatedDir}
                        </div>
                      </div>
                      <div
                        className="commit-change-discard"
                        onClick={(e) => {
                          discardChanges(filename);
                        }}
                      >
                        <ResetIcon transform="rotate(-20)" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <CleanAndDiscardGitChangesModal
              discardChanges={discardChanges}
              loadingDiscard={loadingDiscard}
              uncommittedChangedFiles={uncommittedChangedFiles}
            />
          </div>,
        ]);
      }
    } else if (
      (localAheadBy || (!remoteExists && !repoNotConnected)) &&
      !repoNotConnected
    ) {
      // Push section
      toReturn = toReturn.concat([
        // @ts-expect-error TS2769
        <div className="p-4 flex flex-col" key="push-section">
          <Button
            variant="outline"
            className="bg-secondary-200 space-x-1"
            onClick={push}
            disabled={remoteAheadBy}
            key="push-button"
            size="sm"
            data-tooltip-id="tooltip"
            data-tooltip-content={`Push changes to ${
              gitStatus?.local?.current || "current branch"
            }`}
          >
            {loadingPush && (
              <Spinner className="w-3 h-3 text-secondary-600 fill-secondary-200" />
            )}
            <span>Push</span>
          </Button>
          <div
            className="text-muted-foreground text-2xs"
            key="push-changes-text"
          >
            {!remoteExists
              ? `Push new branch`
              : `You have ${localAheadBy} ${
                  localAheadBy > 1 ? "commits" : "commit"
                } to push${
                  remoteAheadBy
                    ? ", but you need to pull from remote first"
                    : ""
                }`}
          </div>
        </div>,
      ]);
    }

    return toReturn;
  };

  const Spinner = ({ className = "" }) => {
    return (
      <svg
        aria-hidden="true"
        className={"animate-spin " + className}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    );
  };

  const disconnectRepo = async () => {
    if (!project || !project.id) return;

    try {
      setDisconnectingRepo(true);

      if (!session) return;
      const supabaseJWT = await getUnexpiredToken(session);

      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/git/disconnectRepo`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      dispatch(resetGithubState());
      triggerRefetchProjects?.();

      toast.success("Repository disconnected successfully");

      track("GIT_CLEAN");
    } catch (e) {
      logAxiosError("Error disconnecting repository", e);
      toast.error("Error disconnecting repository: " + getAxiosErorrMessage(e));
    } finally {
      setDisconnectingRepo(false);
    }
  };

  const handleRepoCreated = () => {
    if (!session || !canvasId) return;
    triggerRefetchProjects?.();
    dispatch(fetchGitStatus({ session, canvasId }));
  };

  return (
    <div className="git-settings divide-y divide-y-secondary-800">
      <div className="flex flex-row justify-start items-center py-4 px-2">
        <div className="text-secondary-900 text-xs pr-2 font-semibold">
          {!project ? "Loading..." : project?.name || project?.repoPath}
        </div>
        {project && (
          <Link2
            onClick={() => {
              if (
                !project?.provider ||
                project.provider === GitProvider.GITHUB
              ) {
                window.open(`https://github.com/${project?.repoPath}`);
              } else if (project?.provider === GitProvider.GITLAB) {
                window.open(`https://gitlab.com/${project?.repoPath}`);
              }
            }}
            style={{
              cursor: "pointer",
              boxSizing: "initial",
            }}
            width={18}
            height={18}
            className="max-w-none text-secondary-800"
          />
        )}
        {project && !repoNotConnected && !isEmailOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="ml-auto">
              <div className="cursor-pointer">
                <EllipsisIcon className="w-4 h-4 text-secondary-800 ml-auto" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={() => setShowDisconnectConfirmModal(true)}
                disabled={disconnectingRepo}
                className="cursor-pointer"
              >
                {disconnectingRepo ? (
                  <Spinner className="mr-2 h-4 w-4" />
                ) : (
                  <Unlink className="mr-2 h-4 w-4" />
                )}
                <span>Disconnect Repo</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {showPushToRepoModal && (
          <CreateRepositoryModal
            canvasId={canvasId}
            isOpen={showPushToRepoModal}
            onClose={() => {
              setShowPushToRepoModal(false);
              setOriginatingProviderForModal(null);
            }}
            initialSelectedOrg={initialModalOrg}
            onRepoCreated={handleRepoCreated}
            originatingProvider={originatingProviderForModal}
          />
        )}
      </div>

      {showDisconnectConfirmModal && (
        <DisconnectRepoModal
          isOpen={showDisconnectConfirmModal}
          onClose={() => setShowDisconnectConfirmModal(false)}
          onConfirm={disconnectRepo}
          isDisconnecting={disconnectingRepo}
        />
      )}

      {repoNotConnected && (
        <div className="flex flex-col items-center justify-center gap-y-2 p-4">
          <div className="w-full flex flex-row text-xs text-secondary-800 text-left gap-x-1">
            {remoteAheadBy && !repoNotConnected ? (
              <div
                className="flex flex-row gap-x-1 text-left hover:text-secondary-900 cursor-pointer"
                onClick={pull}
              >
                <span>{`${remoteAheadBy} commits to pull`}</span>
                <DownloadIcon className="hover:text-secondary-900 cursor-pointer" />
              </div>
            ) : (
              <></>
            )}
          </div>
          <div
            className={cn(
              "bg-secondary-200 w-full flex flex-row px-4 py-2 gap-x-2 items-center justify-center rounded-lg cursor-pointer hover:bg-secondary-300",
              !isGithubConnected &&
                "opacity-40 cursor-not-allowed hover:bg-secondary-200"
            )}
            data-tooltip-id="tooltip"
            data-tooltip-html={
              !isGithubConnected
                ? "A Github account is required to create a repository.<br />Please go to your User Settings to connect your Github account."
                : ""
            }
            onClick={() => {
              if (!isGithubConnected) return;
              setInitialModalOrg(
                userGithubOrgs.length > 0 ? userGithubOrgs[0] : null
              );
              setOriginatingProviderForModal(GitProvider.GITHUB);
              setShowPushToRepoModal(true);
            }}
          >
            <div className="font-bold text-[small] text-text-primary">
              Create repository
            </div>
            <div className="justify-center items-center">
              <GitHubLogoIcon className="text-text-primary" />
            </div>
          </div>
        </div>
      )}
      {repoNotConnected && (
        <div className="flex flex-col items-center justify-center gap-y-2 p-4">
          <div className="w-full flex flex-row text-xs text-secondary-800 text-left gap-x-1">
            {remoteAheadBy && !repoNotConnected ? (
              <div
                className="flex flex-row gap-x-1 text-left hover:text-secondary-900 cursor-pointer"
                onClick={pull}
              >
                <span>{`${remoteAheadBy} commits to pull`}</span>
                <DownloadIcon className="hover:text-secondary-900 cursor-pointer" />
              </div>
            ) : (
              <></>
            )}
          </div>
          <div
            className={cn(
              "bg-secondary-200 w-full flex flex-row px-4 py-2 gap-x-2 items-center justify-center rounded-lg cursor-pointer hover:bg-secondary-300",
              !isGitlabConnected &&
                "opacity-40 cursor-not-allowed hover:bg-secondary-200"
            )}
            data-tooltip-id="tooltip"
            data-tooltip-html={
              !isGitlabConnected
                ? "A Gitlab account is required to create a repository.<br />Please go to your User Settings to connect your Gitlab account."
                : ""
            }
            onClick={() => {
              if (!isGitlabConnected) return;
              setInitialModalOrg(
                userGitlabOrgs.length > 0 ? userGitlabOrgs[0] : null
              );
              setOriginatingProviderForModal(GitProvider.GITLAB);
              setShowPushToRepoModal(true);
            }}
          >
            <div className="font-bold text-[small] text-text-primary">
              Create repository
            </div>
            <div className="justify-center items-center">
              <img
                src="/icons/gitlab-logo.svg"
                className="w-4 h-4 min-h-4 min-w-4"
              />
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col text-sm">{getBottomSection()}</div>
    </div>
  );
});

export default GitSettings;
